const INTEGRATION_SEARCH_KEYWORDS: Record<string, string[]> = {
  "Custom MCP": [
    "mcp",
    "model context protocol",
    "custom connector",
    "custom tool",
    "自定义连接",
    "自定义工具",
  ],
  "Google Search Console": [
    "gsc",
    "search console",
    "google webmaster",
    "google webmasters",
  ],
  Similarweb: ["similar web", "competitive intelligence", "website traffic"],
  Ahrefs: ["backlinks", "seo", "domain rating"],
  Omnisend: [
    "email marketing",
    "sms marketing",
    "lifecycle marketing",
    "newsletter",
  ],
  Okendo: [
    "reviews",
    "ratings",
    "ugc",
    "survey responses",
    "customer feedback",
  ],
  GoAffPro: [
    "affiliate",
    "affiliate marketing",
    "referrals",
    "commissions",
    "payouts",
    "联盟营销",
    "达人",
    "佣金",
  ],
  UpPromote: [
    "affiliate",
    "affiliate marketing",
    "referrals",
    "commissions",
    "payouts",
    "联盟营销",
    "达人",
    "佣金",
  ],
  Loox: ["reviews", "ratings", "ugc", "photo reviews", "product reviews"],
  TrustWILL: ["loyalty", "rewards", "points", "referrals", "loloyal"],
  NoxInfluencer: [
    "influencer",
    "creator",
    "kol",
    "网红",
    "达人",
    "youtube",
    "tiktok",
    "instagram",
    "creator research",
  ],
  "Insider One": [
    "insider",
    "useinsider",
    "customer engagement",
    "personalization",
  ],
  Emarsys: [
    "sap emarsys",
    "sap engagement cloud",
    "email marketing",
    "customer engagement",
  ],
  "X Ads": ["twitter ads", "twitter x", "twitter"],
  "YouTube Ads": ["youtube", "youtube channel", "youtube content"],
  "Snapchat Ads": ["snap", "snapchat"],
  Feishu: ["team collaboration", "docs", "sheets", "calendar", "bitable"],
  DingTalk: [
    "dingding",
    "钉钉",
    "team collaboration",
    "docs",
    "documents",
    "sheets",
    "aitable",
    "smart table",
  ],
  Figma: [
    "design",
    "design system",
    "figma file",
    "components",
    "comments",
    "dev resources",
  ],
};

const INTEGRATION_SEARCH_GROUPS: ReadonlyArray<{
  tools: readonly string[];
  keywords: readonly string[];
}> = [
  {
    tools: [
      "Klaviyo",
      "Mailchimp",
      "Insider One",
      "Emarsys",
      "Attentive",
      "Postscript",
      "Omnisend",
      "Sendlane",
      "ActiveCampaign",
      "Constant Contact",
    ],
    keywords: ["邮件营销", "邮件", "短信营销", "营销自动化"],
  },
  {
    tools: [
      "Zendesk",
      "Intercom",
      "HubSpot",
      "Salesforce",
      "Gorgias",
      "Freshdesk",
      "Help Scout",
      "Pipedrive",
    ],
    keywords: ["客服", "客户服务", "客户关系", "crm"],
  },
  {
    tools: [
      "ShipStation",
      "ShipBob",
      "Deliverr",
      "EasyShip",
      "UPS",
      "FedEx",
      "USPS",
      "DHL",
    ],
    keywords: ["物流", "履约", "配送", "发货"],
  },
  {
    tools: [
      "Stripe",
      "PayPal",
      "QuickBooks",
      "Xero",
      "Square Payments",
      "Braintree",
      "FreshBooks",
      "Wave",
    ],
    keywords: ["对账", "支付", "财务", "会计"],
  },
];

const normalizeSearchText = (value: string) =>
  value
    .replace(/([a-z\d])([A-Z])/g, "$1 $2")
    .toLowerCase()
    .trim()
    .replace(/[^a-z\d\u4e00-\u9fff]+/g, " ")
    .replace(/\s+/g, " ");

const hasAtMostOneDifferentCharacter = (left: string, right: string) => {
  if (left.length !== right.length) return false;

  let differences = 0;
  for (let index = 0; index < left.length; index += 1) {
    if (left[index] !== right[index]) {
      differences += 1;
      if (differences > 1) return false;
    }
  }
  return differences === 1;
};

const matchesIntegrationSearchCandidate = (
  candidate: string,
  query: string,
) => {
  const normalizedCandidate = normalizeSearchText(candidate);
  const normalizedQuery = normalizeSearchText(query);

  if (!normalizedQuery) return true;

  const compactCandidate = normalizedCandidate.replace(/\s/g, "");
  const compactQuery = normalizedQuery.replace(/\s/g, "");
  const isDirectMatch =
    normalizedCandidate.startsWith(normalizedQuery) ||
    normalizedCandidate.includes(` ${normalizedQuery}`) ||
    normalizedCandidate
      .split(" ")
      .some((token) => token.startsWith(normalizedQuery)) ||
    compactCandidate.startsWith(compactQuery);

  if (isDirectMatch) return true;

  return (
    normalizedQuery.length >= 4 &&
    normalizedCandidate
      .split(" ")
      .some((token) =>
        hasAtMostOneDifferentCharacter(
          token.slice(0, normalizedQuery.length),
          normalizedQuery,
        ),
      )
  );
};

function getIntegrationSearchKeywords(tool: string): string[] {
  return [
    ...(INTEGRATION_SEARCH_KEYWORDS[tool] ?? []),
    ...INTEGRATION_SEARCH_GROUPS.filter((group) =>
      group.tools.includes(tool),
    ).flatMap((group) => group.keywords),
  ];
}

export function matchesIntegrationSearch(
  tool: string,
  query: string,
  additionalCandidates: readonly string[] = [],
): boolean {
  if (!query.trim()) return true;

  return [
    tool,
    ...getIntegrationSearchKeywords(tool),
    ...additionalCandidates,
  ].some((candidate) => matchesIntegrationSearchCandidate(candidate, query));
}
